import {
    useEffect,
    useId,
    useRef,
    type KeyboardEvent,
    type ReactElement,
    type ReactNode,
    type SyntheticEvent,
} from 'react';

import styles from './Sheet.module.css';

export function Sheet({
    title,
    open,
    onClose,
    children,
}: {
    title: string;
    open: boolean;
    onClose: () => void;
    children: ReactNode;
}): ReactElement | null {
    const dialog = useRef<HTMLDialogElement>(null);
    const titleId = useId();

    useEffect(() => {
        const node = dialog.current;
        if (node === null) {
            return;
        }
        node.setAttribute('closedby', 'any');
        if (open && !node.open) {
            node.showModal();
            node.focus();
        }
        return () => {
            if (node.open) {
                node.close();
            }
        };
    }, [open]);

    function handleClose(): void {
        onClose();
    }

    function handleCancel(event: SyntheticEvent<HTMLDialogElement>): void {
        event.preventDefault();
        onClose();
    }

    function handleBackdrop(event: SyntheticEvent<HTMLDialogElement>): void {
        if (event.target === dialog.current) {
            onClose();
        }
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDialogElement>): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
        }
    }

    if (!open) {
        return null;
    }

    return (
        <dialog
            ref={dialog}
            className={styles.sheet}
            aria-labelledby={titleId}
            onClose={handleClose}
            onCancel={handleCancel}
            onClick={handleBackdrop}
            onKeyDown={handleKeyDown}
        >
            <div
                className={styles.panel}
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <div className={styles.handle} aria-hidden="true" />
                <h2 id={titleId} className={styles.title}>
                    {title}
                </h2>
                <div className={styles.body}>{children}</div>
            </div>
        </dialog>
    );
}
